import { pathOr, pickAll } from 'ramda';
import type { BundleType } from '@ecomm/shop/models';
import type { ProductBlock } from '../models.generated';
import { useProductBlockQuery } from '../queries/contentTypes/productBlock.generated';
import type { CtaWithUrlData } from './types';
import { transformCtaWithUrlData } from './useCtaWithUrlData';
import type { ImageType } from './useImageData';
import { transformImageData } from './useImageData';

export type ProductBlockData = {
  key?: string;
  eyebrow?: string;
  title: string;
  subhead?: string;
  image: ImageType;
  textBackground?: ImageType;
  ctaWithUrl: CtaWithUrlData;
  featuredProduct?: BundleType;
  financingStyle?: 'Summary with link' | 'Total | Monthly';
  subCtaText?: string;
};

const transformCtaWithUrl = (ctaWithUrl: CtaWithUrlData) =>
  transformCtaWithUrlData({ data: { ctaWithUrl } });

// Return undefined in transformed data instead of null
const transformOptionalData = <T>(data: T | null | undefined) => {
  if (data !== null) {
    return data;
  }
  return undefined;
};

export const transformProductBlockData = (response: object): ProductBlockData => {
  const productBlockData = pathOr([], ['data', 'productBlock'], response);

  const {
    eyebrow,
    title,
    subhead,
    image = {
      mobile: { description: '', url: '' },
      tablet: { description: '', url: '' },
      desktop: { description: '', url: '' },
      desktopLarge: { description: '', url: '' },
    },
    textBackground,
    ctaWithUrl,
    featuredProduct,
    financingStyle,
    subCtaText,
    key,
  }: ProductBlockData = pickAll(
    [
      'eyebrow',
      'title',
      'subhead',
      'image',
      'textBackground',
      'ctaWithUrl',
      'featuredProduct',
      'financingStyle',
      'subCtaText',
      'key',
    ],
    productBlockData,
  );
  const fallbackText = `ProductBlock: ${key}`;
  const transformedImage = transformImageData({ data: { image } });
  const transformedBackground = textBackground
    ? transformImageData({ data: { image: textBackground } }).image
    : transformOptionalData<ImageType>(textBackground);

  return {
    key,
    eyebrow: transformOptionalData<string>(eyebrow),
    title: title || fallbackText,
    subhead: transformOptionalData<string>(subhead),
    ...transformedImage,
    textBackground: transformedBackground,
    ctaWithUrl: transformCtaWithUrl(ctaWithUrl as CtaWithUrlData),
    featuredProduct: transformOptionalData<BundleType>(featuredProduct as BundleType),
    financingStyle: transformOptionalData<'Summary with link' | 'Total | Monthly'>(
      financingStyle,
    ),
    subCtaText: transformOptionalData<string>(subCtaText),
  };
};

const useProductBlockData = (id: ProductBlock): ProductBlockData => {
  const res = useProductBlockQuery({ variables: { id } });

  return transformProductBlockData(res);
};

export default useProductBlockData;
